import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { site } from '../../data/site';
import { Container } from '../ui/Container';
const testimonials = [{
  "id": "claudia",
  "name": "Claudia Valles-Priest",
  "theme": "Coaching that makes a difference",
  "quote": "…really make a difference…",
  "summary": "Claudia credits the instructors’ dedication with her son’s athletic progress and enthusiasm for Taekwondo. She also describes his strong connection with his coach and teammates."
}, {
  "id": "jason",
  "name": "Jason Hammond",
  "theme": "Train together. Grow together.",
  "quote": "Great gym for any age.",
  "summary": "Jason values learning alongside his son: shared training gives them time together while developing useful life skills, with coaching that balances patience and discipline."
}, {
  "id": "tighe",
  "name": "Tighe Burg",
  "theme": "Skills that go beyond the mat",
  "quote": "…leadership and family fun.",
  "summary": "After visiting many schools, Tighe highlights Chee’s instruction in concentration, effort and technique, alongside the broader qualities that make training meaningful for families."
}, {
  "id": "wenona",
  "name": "Wenona Ayarbe",
  "theme": "A place to belong",
  "quote": "The family atmosphere.",
  "summary": "Wenona describes a welcoming school where beginners and committed competitors receive attentive instruction, and where parents can take part in training too."
}, {
  "id": "kerrie",
  "name": "Kerrie Kiefer",
  "theme": "Personal attention. Lasting growth.",
  "quote": "They have matured and love going to class.",
  "summary": "Kerrie saw positive changes at home after enrolling her three sons. She values the way instruction adapts to each child’s level, even within a shared class."
}];
const ROTATION_MS = 9000;
export function ParentFeedback() {
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  useEffect(() => {
    if (reduceMotion) setIsPlaying(false);
  }, [reduceMotion]);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25
    });
    observer.observe(section);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);
  useEffect(() => {
    if (!isPlaying || hovered || !inView || !pageVisible) return;
    const timer = window.setInterval(() => {
      setCurrent(index => (index + 1) % testimonials.length);
    }, ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [isPlaying, hovered, inView, pageVisible, reduceMotion, current]);
  const show = (index: number) => {
    setCurrent((index + testimonials.length) % testimonials.length);
    setIsPlaying(false);
  };
  return <section ref={sectionRef} id="families" aria-label="Bates community testimonials" aria-roledescription="carousel" className="bg-paper py-16 lg:py-24" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocusCapture={event => {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-rotation-control]') && !event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsPlaying(false);
    }
  }} onKeyDown={event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      show(current + (event.key === 'ArrowRight' ? 1 : -1));
    }
  }} data-id="element-200" data-mp="10fp8wvr1en1h">
      <Container data-id="element-201" data-mp="u19x484unwem">
        <div className="grid gap-7 lg:grid-cols-12 lg:gap-10" data-id="element-202" data-mp="yxql92c1uarp">
          <div className="lg:col-span-3" data-id="element-203" data-mp="yy8pal4ydta6">
            <p className="font-display text-lg font-semibold uppercase tracking-[0.12em] text-muted" data-id="element-204" data-mp="1mn1dfvfc2wu8">
              From the Bates community
            </p>
            <p className="mt-3 max-w-[24rem] text-sm leading-relaxed text-muted" data-id="element-205" data-mp="1rlw9hz2bahwa">
              Five perspectives on training, coaching and belonging at Bates.
            </p>
            <a href="/testimonials" className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4 hover:text-brand" data-id="element-206" data-mp="1hvjne2eumgo1">
              More family testimonials
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-207" data-mp="3v3ax0n4eldjb" />
            </a>
          </div>
          <div className="min-w-0 lg:col-span-9" data-id="element-208" data-mp="3q4zmea6aaz01">
            <div className="grid" aria-live={isPlaying ? 'off' : 'polite'} aria-atomic="true" data-id="element-209" data-mp="3q47pmw0yav86">
              {testimonials.map((testimonial, index) => <figure key={testimonial.id} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${testimonials.length}: ${testimonial.name}`} aria-hidden={index !== current} className={`col-start-1 row-start-1 flex min-w-0 flex-col transition-opacity duration-500 motion-reduce:transition-none ${index === current ? 'opacity-100' : 'pointer-events-none select-none opacity-0'}`} data-id="element-210" data-mp="3omzesn4ypix" data-mpi={index}>
                  <p className="font-display text-sm font-semibold uppercase tracking-[0.13em] text-brand" data-id="element-211" data-mp="10dukjzjuewmb">
                    {testimonial.theme}
                  </p>
                  <blockquote className="mt-4 text-balance text-[1.75rem] font-medium leading-[1.25] tracking-[-0.015em] sm:text-[2.25rem] lg:text-[2.7rem]" data-id="element-212" data-mp="3p8kg0ovfgd9q">
                    <p data-id="element-213" data-mp="3u98bbmqovg9r">“{testimonial.quote}”</p>
                  </blockquote>
                  <p className="mt-5 max-w-[48rem] text-base leading-relaxed text-muted sm:text-lg" data-id="element-214" data-mp="31cc7r05oeft">
                    {testimonial.summary}
                  </p>
                  <figcaption className="mt-auto pt-6" data-id="element-215" data-mp="36iceucbafww6">
                    <span className="block font-semibold" data-id="element-216" data-mp="36fq7acoedhdy">{testimonial.name}</span>
                    <span className="mt-1 block text-xs text-muted" data-id="element-217" data-mp="3bgw0scpvt8oj">Excerpt and summary from the school’s testimonials</span>
                  </figcaption>
                </figure>)}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-218" data-mp="3kb2al8gmcg6h">
              <div className="flex items-center gap-1" role="group" aria-label="Choose a testimonial" data-id="element-219" data-mp="2rsnak87wfcro">
                <button type="button" onClick={() => show(current - 1)} aria-label="Previous testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-line transition-colors hover:border-brand hover:text-brand" data-id="element-220" data-mp="2k95l6e7z3sn7">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-221" data-mp="2b6mns9myi88c" />
                </button>
                {testimonials.map((testimonial, index) => <button key={testimonial.id} type="button" onClick={() => show(index)} aria-label={`Show testimonial ${index + 1} of ${testimonials.length}: ${testimonial.name}`} aria-current={index === current ? 'true' : undefined} className="flex h-11 w-8 items-center justify-center rounded-full" data-id="element-222" data-mp="21ajkh0gdc3jt" data-mpi={index}>
                    <span className={`h-2 rounded-full transition-all motion-reduce:transition-none ${index === current ? 'w-5 bg-brand' : 'w-2 bg-muted/35'}`} data-id="element-223" data-mp="1ijsgkef5h5ct" />
                  </button>)}
                <button type="button" onClick={() => show(current + 1)} aria-label="Next testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-line transition-colors hover:border-brand hover:text-brand" data-id="element-224" data-mp="31hdkszgdoqvf">
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-225" data-mp="2sgfg0ey48fbs" />
                </button>
              </div>
              <div className="flex items-center gap-4" data-id="element-226" data-mp="2xhmokg831iji">
                <span className="text-xs tabular-nums text-muted" aria-hidden="true" data-id="element-227" data-mp="2ipuhssmctcte">{current + 1} / {testimonials.length}</span>
                <button type="button" data-rotation-control onClick={() => setIsPlaying(playing => !playing)} aria-label={isPlaying ? 'Pause testimonial rotation' : 'Play testimonial rotation'} className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-ink hover:text-brand" data-id="element-228" data-mp="2nix7uqrxa44u">
                  {isPlaying ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-229" data-mp="24re2weec8g06" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-230" data-mp="24t79jtoyb086" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}